import reportWebVitals from './reportWebVitals';
import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider
} from "react-router-dom";
import { createRoot } from "react-dom/client";
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Onboarding from './pages/Onboarding';
import SsoCallback from './pages/SsoCallback';
import JoinTest from './pages/JoinTest';
import AdminHome from './pages/admin/AdminHome';
import AdminOrganizations from './pages/admin/AdminOrganizations';
import AdminUsers from './pages/admin/AdminUsers';
import ClinicianHome from './pages/clinician/ClinicianHome';
import ClinicianStudents from './pages/clinician/ClinicianStudents';
import ClinicianStudent from './pages/clinician/ClinicianStudent';
import ClinicianClasses from './pages/clinician/ClinicianClasses';
import ClinicianClass from './pages/clinician/ClinicianClass';
import ClinicianReport from './pages/clinician/ClinicianReport';
import ClinicianResults from './pages/clinician/ClinicianResults';
import ClinicianGroups from './pages/clinician/ClinicianGroups';
import ClinicianGroup from './pages/clinician/ClinicianGroup';
import ClinicianTests from './pages/clinician/ClinicianTests';
import TestDELV from './pages/tests/TestDELV';
import TestALL from './pages/tests/TestALL';
import TestWRA from './pages/tests/TestWRA';
import LegalFooter from './components/LegalFooter';
import RouteAccessibility from './components/RouteAccessibility';
import { userHasResultsAccess } from './assessmentAccess';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import './index.css';

const clearSession = () => {
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("portal");
  sessionStorage.removeItem("current_user");
};

const shouldLogoutForResponse = (response: Response, init?: RequestInit) => {
  if (response.status !== 401 || !init?.headers) {
    return false;
  }

  const headers = init.headers;
  if (headers instanceof Headers) {
    const auth = headers.get('Authorization') || headers.get('authorization');
    return !!auth && auth.startsWith('Bearer ');
  }

  if (Array.isArray(headers)) {
    const authHeader = headers.find(([key]) => key.toLowerCase() === 'authorization');
    return !!authHeader && authHeader[1].startsWith('Bearer ');
  }

  const headerRecord = headers as Record<string, string>;
  const auth = headerRecord.Authorization || headerRecord.authorization;
  return typeof auth === 'string' && auth.startsWith('Bearer ');
};

const originalFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const response = await originalFetch(input, init);
  if (shouldLogoutForResponse(response, init)) {
    clearSession();
    document.location.href = "/";
  }
  return response;
};

const renderApp = (router: ReturnType<typeof createBrowserRouter>) => {
  createRoot(document.getElementById("root")!).render(
    <>
      <RouterProvider router={router} />
      <LegalFooter inverse={!sessionStorage.getItem("token")} />
    </>
  );
};

const createAccessibleRouter = (routes: RouteObject[]) => createBrowserRouter([
  {
    element: <RouteAccessibility />,
    children: routes,
  },
]);

const ResultsRoute = () => {
  const currentUser = JSON.parse(sessionStorage.getItem("current_user") || "{}");
  return userHasResultsAccess(currentUser) ? <ClinicianResults /> : <Navigate to="/" replace />;
};

if (sessionStorage.getItem("token") && sessionStorage.getItem("portal") === "admin") {
  const router = createAccessibleRouter([
    {
      path: "/",
      element: (
        <AdminHome />
      ),
    },
    {
      path: "/organizations",
      element: (
        <AdminOrganizations />
      ),
    },
    {
      path: "/users",
      element: (
        <AdminUsers />
      ),
    },
    {
      path: "/students",
      element: (
        <ClinicianStudents />
      ),
    },
    {
      path: "/classes",
      element: (
        <ClinicianClasses />
      ),
    },
    {
      path: "/class/:classId",
      element: (
        <ClinicianClass />
      ),
    },
    {
      path: "/student/:studentId",
      element: (
        <ClinicianStudent />
      ),
    },
    {
      path: "/report/:studentId",
      element: (
        <ClinicianReport />
      ),
    },
    {
      path: "/report/:studentId/:testId",
      element: (
        <ClinicianReport />
      ),
    },
    {
      path: "/results",
      element: (
        <ResultsRoute />
      ),
    },
    {
      path: "/groups",
      element: (
        <ClinicianGroups />
      ),
    },
    {
      path: "/group/:groupId",
      element: (
        <ClinicianGroup />
      ),
    },
    {
      path: "/tests",
      element: (
        <ClinicianTests />
      ),
    },
    {
      path: "/test/DELV/:testId",
      element: (
        <TestDELV />
      ),
    },
    {
      path: "/test/ALL/:testId",
      element: (
        <TestALL />
      ),
    },
    {
      path: "/test/WRA/:testId",
      element: (
        <TestWRA role="Examiner" />
      ),
    },
    {
      path: "/student-test/WRA/:testId",
      element: (
        <TestWRA role="Student" />
      ),
    },
    {
      path: "/join",
      element: (
        <Navigate to="/" replace />
      ),
    },
  ]);

  renderApp(router);
} else if (sessionStorage.getItem("token") && sessionStorage.getItem("portal") === "clinician") {
  const router = createAccessibleRouter([
    {
      path: "/",
      element: (
        <ClinicianHome />
      ),
    },
    {
      path: "/students",
      element: (
        <ClinicianStudents />
      ),
    },
    {
      path: "/classes",
      element: (
        <ClinicianClasses />
      ),
    },
    {
      path: "/class/:classId",
      element: (
        <ClinicianClass />
      ),
    },
    {
      path: "/student/:studentId",
      element: (
        <ClinicianStudent />
      ),
    },
    {
      path: "/report/:studentId",
      element: (
        <ClinicianReport />
      ),
    },
    {
      path: "/report/:studentId/:testId",
      element: (
        <ClinicianReport />
      ),
    },
    {
      path: "/results",
      element: (
        <ResultsRoute />
      ),
    },
    {
      path: "/groups",
      element: (
        <ClinicianGroups />
      ),
    },
    {
      path: "/group/:groupId",
      element: (
        <ClinicianGroup />
      ),
    },
    {
      path: "/tests",
      element: (
        <ClinicianTests />
      ),
    },
    {
      path: "/test/DELV/:testId",
      element: (
        <TestDELV />
      ),
    },
    {
      path: "/test/ALL/:testId",
      element: (
        <TestALL />
      ),
    },
    {
      path: "/test/WRA/:testId",
      element: (
        <TestWRA role="Examiner" />
      ),
    },
    {
      path: "/student-test/WRA/:testId",
      element: (
        <TestWRA role="Student" />
      ),
    },
    {
      path: "/join",
      element: (
        <Navigate to="/" replace />
      ),
    },
  ]);

  renderApp(router);
} else if (sessionStorage.getItem("token") && sessionStorage.getItem("portal") === "student") {
  const router = createAccessibleRouter([
    {
      path: "/",
      element: (
        <Navigate to="/join" replace />
      ),
    },
    {
      path: "/join",
      element: (
        <JoinTest />
      ),
    },
    {
      path: "/student-test/WRA/:testId",
      element: (
        <TestWRA role="Student" />
      ),
    },
  ]);

  renderApp(router);
} else {
  const router = createAccessibleRouter([
    {
      path: "/",
      element: (
        <Login portal="clinician" />
      ),
    },
    {
      path: "/admin",
      element: (
        <Login portal="admin" />
      ),
    },
    {
      path: "/reset-password",
      element: (
        <ResetPassword />
      ),
    },
    {
      path: "/onboarding",
      element: (
        <Onboarding />
      ),
    },
    {
      path: "/sso/callback",
      element: (
        <SsoCallback />
      ),
    },
    {
      path: "/join",
      element: (
        <JoinTest />
      ),
    },
  ]);

  renderApp(router);
}



// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
